import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { NeedsSetup } from "@/components/NeedsSetup";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  CheckCircle, Clock, Play, ListChecks, AlertCircle, 
  Upload, FileText, Image, Video, Trash2, Eye, Loader2, Paperclip
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Process79A = () => {
  const [process, setProcess] = useState(null);
  const [society, setSociety] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [stepDocuments, setStepDocuments] = useState({});
  const [uploadingStep, setUploadingStep] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [currentUploadStep, setCurrentUploadStep] = useState(null);
  const [docName, setDocName] = useState("");
  const [docDescription, setDocDescription] = useState("");
  const [viewDoc, setViewDoc] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const socRes = await fetch(`${API}/societies`, { credentials: "include" });
      const societies = await socRes.json();
      
      if (societies.length > 0) {
        setSociety(societies[0]);
        const [processRes, docsRes] = await Promise.all([
          fetch(`${API}/societies/${societies[0].society_id}/process79a`, { credentials: "include" }),
          fetch(`${API}/societies/${societies[0].society_id}/process79a/documents`, { credentials: "include" })
        ]);
        if (processRes.ok) {
          setProcess(await processRes.json());
        }
        if (docsRes.ok) {
          const docsData = await docsRes.json();
          setStepDocuments(docsData.by_step || {});
        }
      }
    } catch (error) {
      toast.error("Failed to load process data");
    } finally {
      setLoading(false);
    }
  };

  const openUploadDialog = (stepNumber, prefilledDocName = "") => {
    setCurrentUploadStep(stepNumber);
    setDocName(prefilledDocName);
    setDocDescription("");
    setSelectedFile(null);
    setUploadDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size
      const maxSize = file.type.startsWith('video/') ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`File too large. Max: ${maxSize / (1024 * 1024)}MB`);
        return;
      }
      setSelectedFile(file);
      if (!docName) {
        setDocName(file.name.split('.')[0]);
      }
    }
  };

  const uploadDocument = async () => {
    if (!selectedFile || !docName || !currentUploadStep) {
      toast.error("Please select a file and enter a name");
      return;
    }

    setUploadingStep(currentUploadStep);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('name', docName);
    formData.append('description', docDescription);

    try {
      const res = await fetch(
        `${API}/societies/${society.society_id}/process79a/step/${currentUploadStep}/documents`,
        {
          method: "POST",
          body: formData,
          credentials: "include"
        }
      );

      if (res.ok) {
        toast.success("Document uploaded successfully");
        setUploadDialogOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploadingStep(null);
    }
  };

  const deleteDocument = async (docId, stepNumber) => {
    if (!confirm("Are you sure you want to delete this document?")) return;

    try {
      const res = await fetch(
        `${API}/societies/${society.society_id}/process79a/documents/${docId}`,
        {
          method: "DELETE",
          credentials: "include"
        }
      );

      if (res.ok) {
        toast.success("Document deleted");
        fetchData();
      } else {
        toast.error("Failed to delete");
      }
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const viewDocument = async (docId) => {
    try {
      const res = await fetch(
        `${API}/societies/${society.society_id}/process79a/documents/${docId}`,
        { credentials: "include" }
      );
      if (res.ok) {
        const doc = await res.json();
        setViewDoc(doc);
      }
    } catch (error) {
      toast.error("Failed to load document");
    }
  };

  const getMediaIcon = (mediaType) => {
    switch (mediaType) {
      case 'image': return <Image className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const updateStepStatus = async (stepNumber, status) => {
    if (!society) return;
    
    setUpdating(stepNumber);
    try {
      const res = await fetch(
        `${API}/societies/${society.society_id}/process79a/step/${stepNumber}?status=${status}`,
        {
          method: "PUT",
          credentials: "include"
        }
      );

      if (res.ok) {
        const updated = await res.json();
        setProcess(updated);
        toast.success(`Step ${stepNumber} updated to ${status}`);
      } else {
        toast.error("Failed to update step");
      }
    } catch (error) {
      toast.error("Failed to update step");
    } finally {
      setUpdating(null);
    }
  };

  const completedSteps = process?.steps?.filter(s => s.status === "completed").length || 0;
  const totalSteps = process?.steps?.length || 12;
  const progressPercentage = Math.round((completedSteps / totalSteps) * 100);

  const getStepIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-white" />;
      case "in_progress":
        return <Play className="w-5 h-5 text-white" />;
      default:
        return null;
    }
  };

  const getStepClass = (status) => {
    switch (status) {
      case "completed":
        return "step-completed";
      case "in_progress":
        return "step-active";
      default:
        return "step-pending";
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!society) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <NeedsSetup message="Set up your society to track the 79A process." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            79A Process Tracker
          </h1>
          <p className="text-slate-600">Track your redevelopment progress step by step</p>
        </div>

        {/* Progress Overview */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div>
                <p className="text-sm text-slate-600">Overall Progress</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  {progressPercentage}% Complete
                </p>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-600"></div>
                  <span className="text-slate-600">Completed ({completedSteps})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></div>
                  <span className="text-slate-600">In Progress</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-300"></div>
                  <span className="text-slate-600">Pending</span>
                </div>
              </div>
            </div>
            <Progress value={progressPercentage} className="h-3" />
          </CardContent>
        </Card>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {process?.steps?.map((step, idx) => (
            <Card 
              key={idx}
              className={`transition-all ${
                step.status === "in_progress" ? "ring-2 ring-emerald-500 ring-offset-2" : ""
              }`}
              data-testid={`step-card-${step.step}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {/* Step Number */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getStepClass(step.status)}`}>
                    {getStepIcon(step.status) || (
                      <span className="text-sm font-semibold">{step.step}</span>
                    )}
                  </div>
                  
                  {/* Step Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className={`font-semibold ${
                          step.status === "completed" ? "text-slate-900" : 
                          step.status === "in_progress" ? "text-emerald-700" : "text-slate-600"
                        }`} style={{ fontFamily: 'Manrope' }}>
                          {step.title}
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">{step.description}</p>
                      </div>
                      
                      {/* Status Selector */}
                      <Select
                        value={step.status}
                        onValueChange={(value) => updateStepStatus(step.step, value)}
                        disabled={updating === step.step}
                      >
                        <SelectTrigger 
                          className="w-[130px]"
                          data-testid={`step-status-${step.step}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">
                            <div className="flex items-center gap-2">
                              <Clock className="w-3 h-3 text-slate-500" />
                              Pending
                            </div>
                          </SelectItem>
                          <SelectItem value="in_progress">
                            <div className="flex items-center gap-2">
                              <Play className="w-3 h-3 text-emerald-600" />
                              In Progress
                            </div>
                          </SelectItem>
                          <SelectItem value="completed">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-emerald-600" />
                              Completed
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Status Badge */}
                    <div className="mt-3 flex items-center gap-2">
                      {step.status === "completed" && (
                        <span className="badge-ready px-2 py-1 rounded-full text-xs font-medium">
                          Completed
                        </span>
                      )}
                      {step.status === "in_progress" && (
                        <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-1 rounded-full text-xs font-medium animate-pulse">
                          In Progress
                        </span>
                      )}
                      {step.status === "pending" && (
                        <span className="badge-pending px-2 py-1 rounded-full text-xs font-medium">
                          Pending
                        </span>
                      )}
                      {/* Document count badge */}
                      {stepDocuments[step.step]?.length > 0 && (
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <Paperclip className="w-3 h-3" />
                          {stepDocuments[step.step].length}
                        </span>
                      )}
                    </div>

                    {/* Documents Section */}
                    <div className="mt-4 pt-3 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-slate-600">Supporting Documents</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => openUploadDialog(step.step)}
                          data-testid={`upload-btn-step-${step.step}`}
                        >
                          <Upload className="w-3 h-3 mr-1" />
                          Upload
                        </Button>
                      </div>

                      {/* Suggested Documents */}
                      {step.suggested_docs && step.suggested_docs.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs text-slate-500 mb-1">Suggested uploads:</p>
                          <div className="flex flex-wrap gap-1">
                            {step.suggested_docs.map((sug, i) => (
                              <button
                                key={i}
                                onClick={() => {
                                  openUploadDialog(step.step, sug.name);
                                }}
                                className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full hover:bg-emerald-100 transition-colors flex items-center gap-1"
                                data-testid={`suggested-doc-${step.step}-${i}`}
                              >
                                {sug.type === 'image' && <Image className="w-3 h-3" />}
                                {sug.type === 'video' && <Video className="w-3 h-3" />}
                                {sug.type === 'document' && <FileText className="w-3 h-3" />}
                                {sug.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Document List */}
                      {stepDocuments[step.step]?.length > 0 ? (
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {stepDocuments[step.step].map((doc) => (
                            <div 
                              key={doc.doc_id} 
                              className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {getMediaIcon(doc.media_type)}
                                <span className="truncate">{doc.name}</span>
                                <span className="text-xs text-slate-400">
                                  {formatFileSize(doc.file_size)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  onClick={() => viewDocument(doc.doc_id)}
                                >
                                  <Eye className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                  onClick={() => deleteDocument(doc.doc_id, step.step)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 italic">No documents uploaded yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>
                Upload Document for Step {currentUploadStep}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="doc-file">Select File (Image, Video, or Document)</Label>
                <Input
                  id="doc-file"
                  type="file"
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Max: 10MB for images/docs, 50MB for videos
                </p>
              </div>
              
              {selectedFile && (
                <div className="p-2 bg-slate-50 rounded text-sm flex items-center gap-2">
                  {getMediaIcon(selectedFile.type.split('/')[0])}
                  <span>{selectedFile.name}</span>
                  <span className="text-slate-400">({formatFileSize(selectedFile.size)})</span>
                </div>
              )}

              <div>
                <Label htmlFor="doc-name">Document Name *</Label>
                <Input
                  id="doc-name"
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  placeholder="e.g., Society Resolution Minutes"
                />
              </div>

              <div>
                <Label htmlFor="doc-desc">Description (Optional)</Label>
                <Input
                  id="doc-desc"
                  value={docDescription}
                  onChange={(e) => setDocDescription(e.target.value)}
                  placeholder="Brief description..."
                />
              </div>

              <Button
                className="w-full bg-emerald-700 hover:bg-emerald-800"
                onClick={uploadDocument}
                disabled={!selectedFile || !docName || uploadingStep}
              >
                {uploadingStep ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Document Dialog */}
        <Dialog open={!!viewDoc} onOpenChange={() => setViewDoc(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>{viewDoc?.name}</DialogTitle>
            </DialogHeader>
            {viewDoc && (
              <div className="space-y-4">
                {viewDoc.description && (
                  <p className="text-sm text-slate-600">{viewDoc.description}</p>
                )}
                <div className="text-xs text-slate-500">
                  Uploaded by {viewDoc.uploaded_by_name} • {formatFileSize(viewDoc.file_size)}
                </div>
                
                {/* Preview */}
                <div className="border rounded-lg overflow-hidden">
                  {viewDoc.media_type === 'image' && viewDoc.file_data && (
                    <img 
                      src={`data:${viewDoc.file_type};base64,${viewDoc.file_data}`}
                      alt={viewDoc.name}
                      className="w-full max-h-[60vh] object-contain"
                    />
                  )}
                  {viewDoc.media_type === 'video' && viewDoc.file_data && (
                    <video 
                      controls
                      className="w-full max-h-[60vh]"
                      src={`data:${viewDoc.file_type};base64,${viewDoc.file_data}`}
                    />
                  )}
                  {viewDoc.media_type === 'document' && (
                    <div className="p-8 text-center bg-slate-50">
                      <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">Document preview not available</p>
                      {viewDoc.file_data && (
                        <Button
                          className="mt-4"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `data:${viewDoc.file_type};base64,${viewDoc.file_data}`;
                            link.download = viewDoc.name;
                            link.click();
                          }}
                        >
                          Download Document
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Important Notice */}
        <Card className="bg-amber-50 border-amber-100">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-amber-800" style={{ fontFamily: 'Manrope' }}>
                  Important Notice
                </h3>
                <p className="text-sm text-amber-700 mt-1">
                  The 79A process as per Maharashtra Ownership Flats Act requires strict compliance. 
                  Each step should be documented and approved before proceeding to the next. 
                  Consult with legal experts for accurate guidance.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
